import { useEffect, useState, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { useMaintenance } from './hooks/useMaintenance';
import { PointsProvider } from './components/shared/PointsContext';
import { PageLoader } from './components/shared/Loader';
import MaintenancePage from './components/MaintenancePage';

// Eager load critical components (needed immediately)
import Home from './components/HomeNew';
import LandingPage from './components/LandingPageNew';
import Login from './components/LoginNew';
import PrivacyConsentModal from './components/shared/PrivacyConsentModal';

// Lazy load heavy tool components (load only when needed)
const CardGenerator = lazy(() => import('./components/Aadhaar/CardGeneratorNew'));
const VoterForm = lazy(() => import('./components/Voter/VoterFormNew'));
const PanCardGenerator = lazy(() => import('./components/Pan/PanCardGeneratorNew'));
const SokSandeshForm = lazy(() => import('./components/SokSandesh/SokSandeshFormNew'));

// Lazy load secondary pages
const Privacy = lazy(() => import('./components/PrivacyNew'));
const Terms = lazy(() => import('./components/TermsNew'));
const Contact = lazy(() => import('./components/ContactNew'));
const Settings = lazy(() => import('./components/SettingsNew'));
const Notifications = lazy(() => import('./components/NotificationsNew'));
const AdminPanel = lazy(() => import('./components/AdminPanelNew'));
const Recharge = lazy(() => import('./components/Recharge/RechargeNew'));
const NotFound = lazy(() => import('./components/NotFoundNew'));

function App() {
  const { user, loading, isAdmin } = useAuth();
  const { isMaintenanceMode, maintenanceMessage, estimatedTime } = useMaintenance();
  const location = useLocation();
  const [hasConsent, setHasConsent] = useState(null);
  const [isConsentChecked, setIsConsentChecked] = useState(false);

  useEffect(() => {
    // Check consent status
    const consent = localStorage.getItem('privacy_terms_accepted');
    setHasConsent(!!consent);
    setIsConsentChecked(true);

    // Listen for changes in localStorage (in case user accepts in another tab)
    const onStorage = () => {
      const updated = localStorage.getItem('privacy_terms_accepted');
      setHasConsent(!!updated);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Check if current route is a static route that should bypass consent
  const isStaticRoute = ['/privacy', '/terms', '/contact'].includes(location.pathname);

  // For static routes, render immediately without consent check
  if (isStaticRoute) {
    return (
      <PointsProvider>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/contact" element={<Contact />} />
          </Routes>
        </Suspense>
      </PointsProvider>
    );
  }

  // Show loading spinner while checking consent and auth (only for non-static routes)
  if (!isConsentChecked || loading || user === undefined) {
    return <PageLoader />;
  }

  // Show privacy consent modal if not accepted (only for non-static routes)
  if (!hasConsent) {
    return <PrivacyConsentModal forceShow onAccept={() => setHasConsent(true)} />;
  }

  // MAINTENANCE MODE: Show maintenance page for non-admin users
  // Admin users always bypass so they can test the live site after deploying
  if (isMaintenanceMode && !isAdmin) {
    return <MaintenancePage message={maintenanceMessage} estimatedTime={estimatedTime} />;
  }

  // Check if user is trying to access protected routes while not logged in
  const isProtectedRoute = ['/home', '/aadhaar', '/voter', '/pan', '/soksandesh', '/recharge'].includes(location.pathname);
  const isPublicRoute = ['/', '/login'].includes(location.pathname);

  // Redirect logic for other routes
  if (!user && isProtectedRoute) {
    return <Navigate to="/" replace />;
  }

  if (user && isPublicRoute && location.pathname !== '/') {
    return <Navigate to="/home" replace />;
  }

  // Only force admin to /admin
  if (user && isAdmin && location.pathname !== '/admin') {
    return <Navigate to="/admin" replace />;
  }

  return (
    <PointsProvider>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public Routes - No lazy loading needed */}
          <Route
            path="/"
            element={
              user ? (
                <Navigate to="/home" replace />
              ) : (
                <LandingPage />
              )
            }
          />

          <Route
            path="/login"
            element={
              user ? (
                <Navigate to="/home" replace />
              ) : (
                <Login />
              )
            }
          />

          <Route
            path="/privacy"
            element={<Privacy />}
          />
          <Route
            path="/terms"
            element={<Terms />}
          />
          <Route
            path="/contact"
            element={<Contact />}
          />

          {/* Protected Routes - Lazy loaded for better performance */}
          <Route
            path="/home"
            element={
              user ? (
                <Home />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="/recharge"
            element={
              user ? (
                <Recharge />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          {/* Tool Routes - Heavy components, lazy loaded */}
          <Route
            path="/aadhaar"
            element={
              user ? (
                <CardGenerator />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="/voter"
            element={
              user ? (
                <VoterForm />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="/pan"
            element={
              user ? (
                <PanCardGenerator />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="/soksandesh"
            element={
              user ? (
                <SokSandeshForm />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          {/* Secondary Routes - Lazy loaded */}
          <Route
            path="/settings"
            element={
              user ? (
                <Settings />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/notifications"
            element={
              user ? (
                <Notifications />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route path="/admin" element={user && isAdmin ? <AdminPanel /> : <Navigate to="/" />} />

          {/* 404 - Lazy loaded */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </PointsProvider>
  );
}

export default App;